import React, {Dispatch, memo, SetStateAction, useEffect} from 'react';
import {useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from 'store';
import {nanoid} from '@reduxjs/toolkit';
import {getSpecificCustomer, selectCustomerById} from 'store/customers/customersSlice';
import {
    CUSTOMER_DOCUMENTS,
    useCreateCustomerDocument,
    useGetSpecificCustomerDocuments
} from 'hooks/customer/documents.hooks';

// material-ui
import {useTheme} from '@mui/material/styles';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem, Snackbar,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import {DatePicker, LocalizationProvider} from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';

// locale
import {format, isBefore} from 'date-fns';
import {enUS, he} from 'date-fns/locale';
import {FormattedMessage} from 'react-intl';

// assets
import {IconArchive, IconCirclePlus, IconCurrencyShekel, IconTrash} from '@tabler/icons';
import VisibilityIcon from '@mui/icons-material/Visibility';

// project imports
import config from 'config';
import MainCard from 'ui-component/cards/MainCard';
import DocumentCustomerDetails from '../DocumentCustomerDetails';
import DocumentPreview from '../DocumentPreview';
import CartesetList from 'views/customer/profile/debts';

// types
import {CartesetDataHolder} from 'types/CartesetDataHolder';

import Swal from 'sweetalert2';
import {
    applyCheckMemory,
    Check,
    createDocument,
    Details,
    filterZeroPayments,
    Payment,
    PaymentMethod,
    paymentMethods,
    rememberedCheckFields
} from '../../../../../types/DocumentsTypes/utils';
import {TaxNote} from '../../../../../types/DocumentsTypes/TaxNote';
import {displayDocumentId, ExtendedDocument} from '../../../../../types/DocumentsTypes/Document';
import JSDB from '../../../../../fromKotlin/nk';
import {PayNote} from '../../../../../types/DocumentsTypes/PayNote';
import {dateObjectToString, roundDecimals} from '../../../../../fromKotlin/nkutils';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NoNegative, {WithNegative} from '../../../../../ui-component/textFieldNumber';
import {
    calculateOptimized,
    cancelPaymentLine,
    getClientChecks,
    getSpecificDocument,
    newAllDailyNew,
    newAllOrderNote
} from '../../../../../api/customer/documents.api';
import {number_style} from "../../../../../styles/tablecell";
import {Delete, Image, ImageSearch} from '@mui/icons-material';
import {useQueryClient} from "react-query";
import {orderKey, writeOrdersToOD} from "../../../../../types/OrderData";
import {SNACKBAR_OPEN} from "../../../../../store/actions";
import {getSpecificSupplier, selectSupplierById} from "../../../../../store/suppliers/suppliersSlice";

import PaymentIframeDialog, {PaymentPaidResult} from 'ui-component/PaymentIframeDialog';
import {getTranzilaConfig} from 'api/customer/tranzila.api';
import {BankAutocomplete} from './BankAutocomplete';
import {confirmLargeRefund, refundRestAmount} from './refundRest';

/** Documents a receipt can close: open invoices, and open credit notes that offset them. */
const RECEIPT_DOC_TYPES = ['invoice', 'sup_invoice', 'refund', 'sup_refund'];

const MultiDocumentsActions = ({setReceipts, selected}: { setReceipts: any; selected: CartesetDataHolder[] }) => {
    // An invoice and a credit note can share a number — compare type and id.
    const onAddHandler = () => setReceipts(prev => {
        const have = new Set(prev.map(p => `${p.type}:${p.id}`));
        return prev.concat(selected
            .filter(s => RECEIPT_DOC_TYPES.includes(s.doc.type)
                && (s.doc.status === 'open' || s.doc.status === 'partly_closed')
                && !have.has(`${s.doc.type}:${s.doc.id}`))
            .map(s => s.doc));
    });

    return (
        <Tooltip title={<FormattedMessage id='Choose'/>}>
            <IconButton size='medium' onClick={onAddHandler}>
                <CheckCircleOutlineIcon color='success'/>
            </IconButton>
        </Tooltip>
    );
};

interface PaymentCompProps {
    payment: Payment;
    onPaymentChange: (payment: Payment) => void;
    onPaymentAdd: () => void;
    onPaymentDelete: (id: string) => void;
    addable?: boolean;
    deletable?: boolean;
    images?: {
        id: string;
        file: File;
    }[] | null;
    setImages?: Dispatch<SetStateAction<{
        id: string;
        file: File;
    }[] | null>>;
    /** Called after a successful hosted charge, with the payment line already updated. */
    onCreditCharged?: (updatedPayment: Payment, result: PaymentPaidResult) => void;
    customerId?: string;
    customerDetails?: Details;
}

export const PaymentComp = ({
                                payment,
                                onPaymentChange,
                                onPaymentAdd,
                                onPaymentDelete,
                                addable = false,
                                deletable = false,
                                images,
                                setImages,
                                onCreditCharged,
                                customerId,
                                customerDetails,
                            }: PaymentCompProps) => {
    const theme = useTheme();
    // Hosted-payment readiness still comes from the current Tranzila config until the backend exposes generic readiness.
    const hostedPaymentReady = React.useMemo(() => !!getTranzilaConfig(), []);
    const [chargeOpen, setChargeOpen] = React.useState(false);
    const [charged, setCharged] = React.useState<PaymentPaidResult | null>(null);
    const [ccInstallments, setCcInstallments] = React.useState(1);

    const onCharged = (res: PaymentPaidResult) => {
        setChargeOpen(false);
        setCharged(res);
        const kind = payment.method === 'bit' ? 'סליקת ביט' : 'סליקת אשראי';
        const n = res.installments ?? ccInstallments;
        const installmentsNote = payment.method === 'credit' && n > 1
            ? ` · ${n} תשלומים`
            : '';
        // the transaction index becomes the receipt's אסמכתא
        const updatedPayment: Payment = {
            ...payment,
            check: {...payment.check, id: res.transactionId as any, dueDate: payment.check?.dueDate ?? new Date()},
            notes: `${kind}${res.provider ? ` ${res.provider}` : ''} — אסמכתא ${res.transactionId}${res.last4 ? ` (כרטיס ${res.last4})` : ''}${installmentsNote}`,
        };
        onPaymentChange(updatedPayment);
        onCreditCharged?.(updatedPayment, res);
    };

    const hostedCharge = payment.method === 'credit' || payment.method === 'bit';

    return (
        <Grid container spacing={2}>
            <Grid item xs={4} sm={4}>

                <WithNegative
                    fullWidth
                    id='amount'
                    name='amount'
                    label='סכום'
                    value={payment.amount}
                    changer={(v) => onPaymentChange({
                        ...payment,
                        amount: Number(v)
                    })}
                />
            </Grid>
            <Grid item xs={4} sm={4}>
                <TextField
                    id='outlined-select-currency'
                    select
                    label='בחר שיטת תשלום'
                    fullWidth
                    value={payment.method}
                    onChange={(m) => onPaymentChange({...payment, method: m.target.value as PaymentMethod})}
                >
                    {Object.keys(paymentMethods).map((p) => (
                        <MenuItem key={p} value={p}>
                            {paymentMethods[p].name}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
            {payment.method === 'check' || payment.method=='fastCheck'? (
                <>
                    <Grid item xs={3} sm={3}>
                        <LocalizationProvider dateAdapter={AdapterDateFns} locale={config.i18n === 'he' ? he : enUS}>
                            <DatePicker
                                mask=''
                                renderInput={(props) => <TextField {...props} />}
                                label={<FormattedMessage id='Due Date'/>}
                                value={payment.check?.dueDate}
                                onChange={(newValue) => {
                                    if (newValue) onPaymentChange({
                                        ...payment,
                                        check: {...payment.check, dueDate: newValue}
                                    });
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={2} sm={2}>
                        <NoNegative
                            id='account'
                            fullWidth
                            label='מספר חשבון'
                            value={payment.check?.account ?? 0}

                            changer={(v) =>
                                onPaymentChange({
                                    ...payment,
                                    check: {...payment.check, account: Math.max(0, Number(v))}
                                })
                            }
                        />
                    </Grid>
                    <Grid item xs={2} sm={2}>
                        <NoNegative
                            id='branch'
                            fullWidth
                            label='מספר סניף'
                            value={payment.check?.branch ?? 0}
                            changer={(v) =>
                                onPaymentChange({
                                    ...payment,
                                    check: {...payment.check, branch: Math.max(0, Number(v))}
                                })
                            }
                        />
                    </Grid>
                    <Grid item xs={4} sm={4}>
                        <BankAutocomplete
                            value={payment.check?.bank}
                            onChange={(bank) => onPaymentChange({...payment, check: {...payment.check, bank}})}
                        />
                    </Grid>
                    <Grid item xs={3} sm={3}>
                        <NoNegative
                            id='check'
                            fullWidth
                            label={<FormattedMessage id='Check No.'/>}
                            value={payment.check?.id ?? 0}
                            changer={(v) =>
                                onPaymentChange({
                                    ...payment,
                                    check: {...payment.check, id: Math.max(0, Number(v))}
                                })
                            }
                        />
                    </Grid>
                    <Grid item xs={11} sm={11}>
                        {
                            images?.find((i) => i.id === payment?.id)?.file ? (
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '100%',
                                    width: '100%'
                                }}>
                                    <div>
                                        <img
                                            src={URL.createObjectURL(images?.find((i) => i.id === payment?.id)?.file as File)}
                                            alt='check' style={{
                                            maxWidth: '900px',
                                            maxHeight: '500px',
                                            width: '100%',
                                            height: '100%'
                                        }}/>
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-evenly',
                                        alignItems: 'center',
                                        width: '100%',
                                        gap: '10px'
                                    }}>
                                        <Button
                                            variant='contained'
                                            component='label'
                                            fullWidth
                                            sx={{height: '100%'}}
                                            onClick={() => {
                                                setImages ? setImages(images?.filter((i) => i.id !== payment?.id) ?? []) : undefined;
                                            }}
                                            color='error'
                                        >
                                            <Delete style={{
                                                marginLeft: "10px"
                                            }}/>
                                            מחק תמונה
                                        </Button>
                                        <Button
                                            variant='contained'
                                            component='label'
                                            fullWidth
                                            sx={{height: '100%'}}
                                            onClick={() => {
                                                // CALLBACK
                                            }}
                                        >
                                            <ImageSearch style={{
                                                marginLeft: "10px"
                                            }}/>
                                            זיהוי אוטומטי
                                        </Button>
                                    </div>
                                </Box>
                            ) : (
                                <Button
                                    variant='contained'
                                    component='label'
                                    fullWidth
                                    sx={{height: '100%'}}
                                    onClick={() => {
                                        const input = document.getElementById(`check-imge-${images?.findIndex(i => i?.id === payment?.id)}`);
                                        if (input) input.click();
                                    }}
                                >
                                    <Image style={{
                                        marginLeft: "10px"
                                    }}/>
                                    העלאה תמונה
                                </Button>
                            )
                        }
                        <input type="file" id={`check-imge-${images?.findIndex(i => i?.id === payment?.id)}`}
                               style={{display: "none"}}
                               onChange={(e) => {
                                   if (e.target.files) {
                                       const file = e.target.files[0];
                                       if (file) {
                                           setImages ? setImages([...images ?? [], {
                                               id: payment?.id,
                                               file: file
                                           }]) : undefined;
                                       }
                                   }
                               }}
                        />
                    </Grid>
                </>
            ) : undefined}
            {payment.method === 'deposit' ? (
                <>

                    <Grid item xs={3} sm={3}>
                        <BankAutocomplete
                            value={payment.check?.bank}
                            onChange={(bank) => onPaymentChange({...payment, check: {...payment.check, bank}})}
                        />
                    </Grid>
                    <Grid item xs={3} sm={3}>
                        <LocalizationProvider dateAdapter={AdapterDateFns} locale={config.i18n === 'he' ? he : enUS}>
                            <DatePicker
                                mask=''
                                renderInput={(props) => <TextField {...props} />}
                                label="תאריך העברה"
                                value={payment?.check?.dueDate}
                                onChange={(newValue) => {
                                    if (newValue) onPaymentChange({
                                        ...payment,
                                        check: {...payment.check, dueDate: newValue}
                                    });
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={3} sm={3}>
                        <NoNegative
                            id='check'
                            fullWidth
                            label={'אסמכתא'}
                            value={payment.check?.id ?? 0}
                            changer={(v) =>
                                onPaymentChange({
                                    ...payment,
                                    check: {...payment.check, id: Math.max(0, Number(v))}
                                })
                            }
                        />
                    </Grid>
                </>
            ) : undefined
            }
            {hostedCharge ? (
                <>
                    {payment.method === 'credit' && hostedPaymentReady && !charged ? (
                        <Grid item xs={4} sm={4}>
                            <TextField
                                id='credit-installments'
                                select
                                label='תשלומים'
                                fullWidth
                                value={ccInstallments}
                                onChange={(e) => setCcInstallments(Number(e.target.value) || 1)}
                            >
                                {Array.from({length: 12}, (_, i) => i + 1).map((n) => (
                                    <MenuItem key={n} value={n}>
                                        {n === 1 ? 'תשלום אחד' : `${n} תשלומים`}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    ) : undefined}
                    <Grid item xs={payment.method === 'credit' && hostedPaymentReady && !charged ? 5 : 8} sm={payment.method === 'credit' && hostedPaymentReady && !charged ? 5 : 8}>
                        {charged ? (
                            <Alert severity="success" sx={{alignItems: 'center'}}>
                                הסליקה בוצעה — אסמכתא {charged.transactionId}
                                {charged.last4 ? ` (כרטיס ${charged.last4})` : ''}
                                {charged.provider ? ` — ${charged.provider}` : ''}
                            </Alert>
                        ) : hostedPaymentReady ? (
                            <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                sx={{height: '100%'}}
                                disabled={!payment.amount || payment.amount <= 0 || (payment.method === 'bit' && payment.amount < 5)}
                                onClick={() => setChargeOpen(true)}
                            >
                                {payment.method === 'bit' ? 'בצע סליקת ביט מאובטחת' : 'בצע סליקה מאובטחת'}
                            </Button>
                        ) : (
                            <Alert severity="info">
                                לרישום בלבד — לחיוב {payment.method === 'bit' ? 'ביט' : 'אשראי'} בפועל יש לחבר ספק תשלום (הגדרות ← אשראי)
                            </Alert>
                        )}
                    </Grid>
                    {payment.method === 'bit' && payment.amount > 0 && payment.amount < 5 ? (
                        <Grid item xs={12}>
                            <Alert severity="warning">סכום מינימום לחיוב ביט הוא 5 ש"ח</Alert>
                        </Grid>
                    ) : null}
                    <Grid item xs={3} sm={3}>
                        <NoNegative
                            id='credit-ref'
                            fullWidth
                            label='אסמכתא'
                            value={payment.check?.id ?? 0}
                            changer={(v) =>
                                onPaymentChange({
                                    ...payment,
                                    check: {...payment.check, id: Math.max(0, Number(v))}
                                })
                            }
                        />
                    </Grid>
                    <PaymentIframeDialog
                        open={chargeOpen}
                        amount={payment.amount}
                        purpose="receipt"
                        method={payment.method === 'bit' ? 'bit' : 'credit'}
                        refId={customerId}
                        contact={customerDetails?.companyName}
                        company={customerDetails?.companyName}
                        email={customerDetails?.email}
                        phone={customerDetails?.phone}
                        address={[customerDetails?.street, customerDetails?.streetNumber].filter(Boolean).join(' ')}
                        city={customerDetails?.city}
                        paymentPlan={ccInstallments}
                        allowInstallments={payment.method === 'credit'}
                        onPaid={onCharged}
                        onClose={() => setChargeOpen(false)}
                    />
                </>
            ) : undefined}
            {payment.method === 'withholding_tax' ? (
                <>
                    <Grid item xs={3} sm={3}>
                        <NoNegative
                            id='percent'
                            fullWidth
                            label='אחוז ניכוי'
                            value={payment.check?.percent ?? 0}
                            changer={(v) => {
                                const percent = Math.max(0, Math.min(100, Number(v)));
                                const baseAmount = payment.check?.baseAmount ?? 0;
                                const calculatedAmount = roundDecimals((baseAmount * percent) / 100, 2);
                                onPaymentChange({
                                    ...payment,
                                    amount: calculatedAmount,
                                    check: {...payment.check, percent: percent, baseAmount: baseAmount}
                                });
                            }}
                        />
                    </Grid>
                    <Grid item xs={3} sm={3}>
                        <NoNegative
                            id='baseAmount'
                            fullWidth
                            label='סכום בסיס'
                            value={payment.check?.baseAmount ?? 0}
                            changer={(v) => {
                                const baseAmount = Math.max(0, Number(v));
                                const percent = payment.check?.percent ?? 0;
                                const calculatedAmount = roundDecimals((baseAmount * percent) / 100, 2);
                                onPaymentChange({
                                    ...payment,
                                    amount: calculatedAmount,
                                    check: {...payment.check, percent: percent, baseAmount: baseAmount}
                                });
                            }}
                        />
                    </Grid>
                    <Grid item xs={3} sm={3}>
                        <TextField
                            id='calculatedAmount'
                            fullWidth
                            label='סכום מחושב'
                            value={payment.amount.toFixed(2)}
                            InputProps={{
                                readOnly: true,
                                startAdornment: (
                                    <InputAdornment position='start'>
                                        <IconCurrencyShekel size={12}/>
                                    </InputAdornment>
                                )
                            }}
                            variant='outlined'
                        />
                    </Grid>
                </>
            ) : undefined
            }
            <Grid item xs={1} sm={1} alignItems='center'>
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        mt: 1,
                        mb: 1,
                        flexDirection: 'row',
                        justifyContent: 'center'
                    }}
                >
                    {addable ? (
                        <Tooltip title={<FormattedMessage id='add'/>}>
                            <IconButton size='small' onClick={onPaymentAdd}>
                                <IconCirclePlus color={theme.palette.primary.main}/>
                            </IconButton>
                        </Tooltip>
                    ) : undefined}
                    {deletable ? (
                        <Tooltip title={<FormattedMessage id='delete'/>}>
                            <IconButton size='small' onClick={() => onPaymentDelete(payment.id)}>
                                <IconTrash color={theme.palette.error.main}/>
                            </IconButton>
                        </Tooltip>
                    ) : undefined}
                </Box>
            </Grid>

        </Grid>

    );
};
const icon = <CheckBoxOutlineBlankIcon fontSize='small'/>;
const checkedIcon = <CheckBoxIcon fontSize='small'/>;
const NewReceipt = ({
                        upid,
                        narrow,
                        refId,
                        types,sup,
                        onIssued
                    }: { upid: string, narrow?: boolean, refId?: number[], types?: any[],sup?:boolean, onIssued?: () => void }) => {
    const theme = useTheme();
    const {id} = useParams();
    const dispatch = useDispatch();
    // TODO: Change to single customer fetching
    const customer = sup ? useSelector((state: RootState) => selectSupplierById(state, id ?? upid)) : useSelector((state: RootState) => selectCustomerById(state, id ?? upid));
    const [details, setDetails] = React.useState<Details>({
        city: '', companyId: '', companyName: '', email: '', id: 0, phone: '', street: '', streetNumber: ''


    });
    const [comment, setComment] = React.useState<string>('');
    const checkDetailsRef = React.useRef<Check | undefined>(undefined);
    React.useEffect(() => {
        if (!customer) {
            if (sup) dispatch(getSpecificSupplier(Number(id ?? upid)));
            else dispatch(getSpecificCustomer(Number(id ?? upid)));
            return;
        }
        setDetails({
            city: customer!.city,
            companyId: customer!.businessId,
            companyName: customer!.businessName,
            email: customer!.email,
            id: Number(customer!.id),
            phone: customer!.phone,
            street: customer!.street,
            streetNumber: customer!.streetNumber
        });
    }, [customer, dispatch, sup, id, upid]);

    const createCustomerDocument = useCreateCustomerDocument(id ?? upid,sup);

    const [date, setDate] = React.useState<Date | null>(new Date());
    const [payments, setPayments] = React.useState<Payment[]>([{
        id: nanoid(),
        amount: 0,
        method: 'cash',
        check: {
            dueDate: new Date(),
        }
    }]);
    const [receipts, setReceipts] = React.useState<TaxNote[]>([]);
    const [previewOpen, setPreviewOpen] = React.useState<{ document?: ExtendedDocument; open: boolean }>({open: false});
    const commentchange = (e) => {
        setComment(e.target.value);
    };
    const [taxNotes, setTaxNotes] = React.useState<ExtendedDocument[]>([]);
    const pool = sup ? "SUP_DOCUMENTS_FOR_PAY" :"CUSTOMER_DOCUMENTS_FOR_PAY"
    const [appliedRef, setAppliedRef] = React.useState(false);
    /** Once the user edits any payment line, choosing invoices must not overwrite checks/amounts/methods. */
    const paymentsUserEditedRef = React.useRef(false);
    const markPaymentsUserEdited = React.useCallback(() => {
        paymentsUserEditedRef.current = true;
    }, []);
    React.useEffect(() => {
        paymentsUserEditedRef.current = false;
        let cancelled = false;
        const x = async () => {
            try {
                const lastCheck = await getClientChecks(Number(id ?? upid), sup);
                if (cancelled) return;
                const memory = lastCheck?.check;
                checkDetailsRef.current = memory;
                setPayments((prevState) => prevState.map((p) => applyCheckMemory(p, memory)));
            } catch {
                if (cancelled) return;
                checkDetailsRef.current = undefined;
            }
        };
        x();
        return () => {
            cancelled = true;
        };
    }, [sup, id, upid]);
    // fetch events data
    // eslint-disable-next-line prefer-const
    const cache = useQueryClient();
    const [totalDebtExist, setDebtExist] = React.useState(undefined);
    React.useEffect(() => {
        cache.removeQueries(pool);
    }, []);
    const {
        data: notes,
        isLoading: isLoading,
        refetch
    } = useGetSpecificCustomerDocuments(sup ? ['sup_invoice', 'sup_refund']:['invoice','refund'], Number(id ?? upid), undefined, undefined, true, undefined, true, undefined, undefined, undefined, undefined, undefined, undefined, {
        keepPreviousData: false,
        staleTime: 0,
        refetchOnMount: 'always',
    }, pool);
    React.useEffect(() => {
        refetch();
        if (!customer) return;
        const cartesetId = Number(customer.nkObject?.masterBranch?.id ?? customer.nkObject?.id);
        if (!Number.isFinite(cartesetId)) return;
        calculateOptimized(null, date, cartesetId, sup).then((x) => {
            setDebtExist(x);
        });
    }, [dispatch, sup, date, customer, refetch]);

    React.useEffect(() => {
        if (notes) {
            setTaxNotes(notes.filter((s) => (s.status === 'open'|| s.status=="partly_closed") && (s.type == 'invoice' || s.type == 'refund' || s.type == 'sup_invoice' || s.type == 'sup_refund')))
            if (!appliedRef && refId && types) {
                let rr = []
                notes?.forEach((dn) => {

                    refId.find((r, index) => {
                        if (r === dn.id && (dn.status === 'open' || dn.status=="partly_closed") && (dn.type == types[index])) {
                            rr.push(dn)
                        }
                    })
                })
                setAppliedRef(true)
                setReceipts(rr)
                return;

            }

        }
        ;
    }, [notes]);

    const receiptsPaymentSyncKey = React.useMemo(
        () =>
            receipts.length === 0
                ? 'empty'
                : `${receipts.map((r) => `${r.id}:${r.value_left}`).sort().join('|')}_${roundDecimals(
                      receipts.reduce(
                          (acc, p) =>
                              acc + (p.type === 'invoice' || p.type === 'sup_invoice' ? p.value_left : -p.value_left),
                          0
                      ),
                      2
                  )}`,
        [receipts]
    );

    // Marked invoices → first line מזומן with full sum; new lines get remainder. Withholding-only override when used.
    // Skipped entirely if the user already edited payments (checks, amounts, methods, add/delete lines).
    React.useEffect(() => {
        if (paymentsUserEditedRef.current) {
            return;
        }
        if (receipts.length === 0) {
            setPayments((prev) => [
                {
                    id: prev[0]?.id ?? nanoid(),
                    amount: 0,
                    method: 'cash',
                    check: rememberedCheckFields(checkDetailsRef.current, {
                        ...prev[0]?.check,
                        dueDate: new Date(),
                    }),
                },
            ]);
            return;
        }
        const invoiceTotal = roundDecimals(
            receipts.reduce(
                (acc, p) => acc + (p.type === 'invoice' || p.type === 'sup_invoice' ? p.value_left : -p.value_left),
                0
            ),
            2
        );
        setPayments((prevPayments) => {
            const hasWithholding = prevPayments.some((p) => p.method === 'withholding_tax');
            if (hasWithholding) {
                return prevPayments.map((payment) => {
                    if (payment.method === 'withholding_tax') {
                        const percent = payment.check?.percent ?? 0;
                        const calculatedAmount = roundDecimals((invoiceTotal * percent) / 100, 2);
                        return {
                            ...payment,
                            amount: calculatedAmount,
                            check: {
                                ...payment.check,
                                baseAmount: invoiceTotal,
                                percent,
                            },
                        };
                    }
                    return payment;
                });
            }
            return [
                {
                    id: nanoid(),
                    amount: invoiceTotal,
                    method: 'cash',
                    check: rememberedCheckFields(checkDetailsRef.current, {
                        ...prevPayments[0]?.check,
                        dueDate: new Date(),
                    }),
                },
            ];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- sync only when selected invoices change; cheque memory comes from checkDetailsRef
    }, [receiptsPaymentSyncKey]);

    const onPaymentChange = React.useCallback(
        (payment: Payment) => {
            markPaymentsUserEdited();
            setPayments((prevPayments) => prevPayments.map((pmt) => {
                if (payment.id !== pmt.id) return pmt;
                const switchedToBank =
                    payment.method !== pmt.method &&
                    (payment.method === 'check' || payment.method === 'fastCheck' || payment.method === 'deposit');
                return switchedToBank ? applyCheckMemory(payment, checkDetailsRef.current) : payment;
            }));
        },
        [markPaymentsUserEdited]
    );

    const onPaymentAdd = React.useCallback(
        () => {
            markPaymentsUserEdited();
            setPayments((prevPayments) => {
                const invoiceTotal = roundDecimals(
                    receipts.reduce(
                        (acc, p) =>
                            acc + (p.type === 'invoice' || p.type === 'sup_invoice' ? p.value_left : -p.value_left),
                        0
                    ),
                    2
                );
                const paidSoFar = roundDecimals(prevPayments.reduce((acc, p) => acc + p.amount, 0), 2);
                const remainder = Math.max(0, roundDecimals(invoiceTotal - paidSoFar, 2));
                const last = prevPayments[prevPayments.length - 1];
                return prevPayments.concat({
                    id: nanoid(),
                    amount: remainder,
                    method: last?.method ?? 'cash',
                    check: rememberedCheckFields(checkDetailsRef.current, {
                        dueDate: new Date(),
                        bank: last?.check?.bank,
                        account: last?.check?.account,
                        branch: last?.check?.branch,
                    }),
                });
            });
        },
        [markPaymentsUserEdited, receipts]
    );

    const onPaymentDelete = React.useCallback(
        (pid: string) => {
            markPaymentsUserEdited();
            setPayments((prevPayments) => prevPayments.filter((p) => p.id !== pid));
        },
        [markPaymentsUserEdited]
    );

    const [refundRest, setRefundRest] = React.useState<boolean>(false);
    const [openCustomerArchive, setOpenCustomerArchive] = React.useState(false);
    const openCustomerArchiveHandler = React.useCallback(() => setOpenCustomerArchive((p => !p)), []);
    const onPaymentCHangeReal = (createCustomerDocument.data) ? undefined : onPaymentChange;
    const [displayEmptyErr, setDisplayEmptyErr] = React.useState<boolean>(false);
    const [images, setImages] = React.useState<{
        id: string;
        file: File;
    }[] | null>(null);
    const [creditRecepitValue, setCreditRecepitValue] = React.useState<boolean>(false);

    const issueReceipt = React.useCallback(
        async (paymentsForIssue: Payment[]) => {
            if (receipts.length === 1 && receipts[0].type === 'refund') {
                setDisplayEmptyErr(true);
                return;
            }
            if (createCustomerDocument.isSuccess || createCustomerDocument.isLoading) return;
            // Drop zero-value payment lines before parsing/saving.
            const nonZeroPayments = filterZeroPayments(paymentsForIssue);
            const total = roundDecimals(nonZeroPayments.reduce((acc, p) => acc + p.amount, 0), 4);
            if (total === 0 || nonZeroPayments.length === 0) return;
            if (creditRecepitValue
                && !(await confirmLargeRefund(refundRestAmount(receipts, total), theme.palette.primary.dark))) {
                return;
            }
            const pay = createDocument({
                comment,
                mainValue: total,
                details,
                date: date ?? new Date(),
                type: sup ? 'sup_receipt' : 'receipt',
                receiptsIds: receipts.map((d) =>
                    d.type === 'invoice' || d.type === 'sup_invoice' ? d.id : -d.id
                ),
                payments: nonZeroPayments,
                entId: id,
                status: 'approved',
                refundRest: refundRest,
            });
            pay.refundRest = creditRecepitValue;
            createCustomerDocument.mutate(pay, {
                onSuccess: () => {
                    onIssued?.();
                }
            });
        },
        [
            receipts,
            createCustomerDocument,
            comment,
            details,
            date,
            sup,
            id,
            refundRest,
            creditRecepitValue,
            onIssued,
            theme.palette.primary.dark,
        ]
    );

    const onCreditCharged = React.useCallback(
        (updatedPayment: Payment, res: PaymentPaidResult) => {
            const paymentsForIssue = payments.map((p) =>
                p.id === updatedPayment.id ? updatedPayment : p
            );
            Swal.fire({
                title: 'הסליקה בוצעה בהצלחה',
                html: [
                    res.transactionId ? `אסמכתא: <b>${res.transactionId}</b>` : '',
                    res.last4 ? `כרטיס: •••• ${res.last4}` : '',
                    'האם להנפיק את הקבלה עכשיו או להמשיך ידנית?',
                ]
                    .filter(Boolean)
                    .join('<br/>'),
                icon: 'success',
                showCancelButton: true,
                confirmButtonColor: theme.palette.primary.dark,
                cancelButtonColor: theme.palette.grey[500],
                confirmButtonText: 'הנפק קבלה',
                cancelButtonText: 'המשך ידנית',
                reverseButtons: true,
            }).then((result) => {
                if (result.isConfirmed) {
                    issueReceipt(paymentsForIssue);
                }
            });
        },
        [payments, issueReceipt, theme.palette.primary.dark, theme.palette.grey]
    );

    const getOptionLabel = (option) =>
        `${option.name || option.title} ${displayDocumentId(option)} - ${option.entName} - ${format(option.date, 'PPP', {
            locale: config.i18n === 'he' ? he : enUS
        })}  - ${option.value_left*((option.type=='invoice' || option.type=='sup_invoice')? 1:-1)} ש''ח `;

    const onPreviewOpen = React.useCallback((document) => (e) => setPreviewOpen({document, open: true}), []);
    const onPreviewClose = () => setPreviewOpen((preview) => ({...preview, open: false}));
    if (!customer || !id && !upid) return <Box/>;

    return (
        <MainCard
            title={
                <Typography variant='h3'>
                    <FormattedMessage id='Create new receipt'/>
                </Typography>
            }
            secondary={
                <>
                    <Tooltip onClick={openCustomerArchiveHandler} title={<FormattedMessage id='Customer Archive'/>}>
                        <Chip icon={<IconArchive size={26}/>} sx={{
                            ml: 0.5, mr: 0.5, '& .MuiChip-icon': {
                                color: '#000080',
                            },
                        }} label="כרטסת לקוח" variant="outlined"/>
                    </Tooltip>
                    <Dialog
                        fullWidth
                        maxWidth='lg'
                        scroll='body'
                        open={openCustomerArchive}
                        onBackdropClick={openCustomerArchiveHandler}>
                        <CartesetList cid={id ?? upid} rowsPerPage={10} withSelection selectedInit={receipts} sup={sup}
                                      pool={pool}
                                      selectionOptions={{
                                          selectedActions: (selected) => <MultiDocumentsActions
                                              setReceipts={setReceipts}
                                              selected={selected}/>
                                      }}/>
                    </Dialog>
                </>
            }
        >
            <Grid container spacing={5}>
                {createCustomerDocument.data === undefined ?
                    <Grid item xs={8} sx={{
                        minWidth: narrow ? '100%' : undefined,
                    }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={12}>
                                <DocumentCustomerDetails customer={customer} date={date} setDate={setDate}/>
                            </Grid>

                            <Grid item xs={12} sm={12}>
                                <TextField
                                    fullWidth
                                    id='comment'
                                    name='comment'
                                    label='היערות'
                                    value={comment}
                                    onChange={commentchange}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <Typography variant='h4'>חשבוניות</Typography>
                            </Grid>
                            {totalDebtExist !== undefined  && (
                                <Grid item xs={12} sm={12}>
                                    <Alert
                                        severity={totalDebtExist > 0 ? "error" : "info"}

                                        sx={{
                                            backgroundColor: totalDebtExist > 0 ? '#ffebee' : theme.palette.info.light,
                                            '& .MuiAlert-message': {
                                                width: '100%'
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                                    {totalDebtExist > 0 ? 'חוב כרטסת ללקוח:' : 'יתרת זכות ללקוח:'}
                                                </Typography>
                                                <Typography variant="h4" sx={{ fontWeight: 'bold', color: totalDebtExist > 0 ? '#c62828' : theme.palette.info.dark }}>
                                                    {Math.abs(totalDebtExist).toFixed(2)} ₪
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="h6"
                                                onClick={openCustomerArchiveHandler}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    color: '#000080',
                                                    cursor: 'pointer',
                                                    textDecoration: 'underline',
                                                    '&:hover': {
                                                        color: '#0000cc'
                                                    }
                                                }}
                                            >
                                                כרטסת לקוח
                                            </Typography>
                                        </Box>
                                    </Alert>
                                </Grid>
                            )}
                            <Grid item xs={12} sm={12}>
                                <FormControlLabel
                                    value='low'
                                    control={
                                        <Checkbox
                                            onChange={(v) => v.target.checked ? setReceipts(taxNotes.filter(d => d.status === 'open' || d.status=='partly_closed')) : setReceipts([])}
                                            color='primary'
                                        />
                                    }
                                    label='בחר את כל חשבוניות המס הפתוחות'
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    id='receipt'
                                    value={receipts}
                                    getOptionLabel={getOptionLabel}
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                    onChange={(event, d) => {
                                        if (d) setReceipts(d);
                                    }}
                                    options={
                                        taxNotes
                                            ?.sort((d1, d2) => ((dateObjectToString(d1.date) == dateObjectToString(d2.date)) ? 0 : isBefore(d1.date, d2.date) ? 1 : -1)) ?? []
                                    }
                                    loading={isLoading}
                                    fullWidth
                                    autoHighlight
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label='בחר חשבונית מס'
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: isLoading ? (
                                                    <CircularProgress color='inherit' size={20}/>
                                                ) : (
                                                    params.InputProps.endAdornment
                                                )
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option, {selected}) => (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                px: 1,
                                                flex: 1,
                                                flexDirection: 'row',
                                                alignItems: 'center'
                                            }}
                                            key={option.id}
                                        >
                                            <li {...props} style={{flexGrow: 1}}>
                                                <Checkbox
                                                    icon={icon}
                                                    checkedIcon={checkedIcon}
                                                    style={{marginRight: 8}}
                                                    checked={selected}
                                                />
                                                {getOptionLabel(option)}
                                            </li>
                                            <Tooltip title={<FormattedMessage id='View'/>}>
                                                <IconButton size='small' onClick={onPreviewOpen(option)}>
                                                    <VisibilityIcon/>
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                />
                            </Grid>
                            {receipts.length > 0 ? <>   <Grid item xs={6} sm={6}>
                                <TextField
                                    id='outlined-read-only-input'
                                    label='סכום חשבוניות'
                                    style={number_style}
                                    variant='standard'
                                    value={roundDecimals(receipts.reduce((acc, p) => acc + (p.type == 'invoice'||p.type=='sup_invoice' ? p.value_left : -p.value_left), 0), 4)}
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                            <InputAdornment position='start'>
                                                <IconCurrencyShekel size={12}/>
                                            </InputAdornment>
                                        )
                                    }}

                                />
                            </Grid>

                                {false ? <Grid item xs={6} sm={6}>
                                    <TextField
                                        id='outlined-read-only-input'
                                        label='סה"כ נותר לתשלום'

                                        variant='standard'

                                        value={roundDecimals(Math.max(0, roundDecimals(roundDecimals(receipts.reduce((acc, p) => acc + p.value_left, 0), 4) - roundDecimals(payments.reduce((acc, p) => acc + p.amount, 0), 4), 4)),2)}
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment position='start'>
                                                    <IconCurrencyShekel size={12}/>
                                                </InputAdornment>
                                            )
                                        }}

                                    />
                                    זכה את היתרה לתשלום
                                    <Checkbox
                                        checked={refundRest}
                                        onChange={(v) => {
                                            setRefundRest(v.target.checked);
                                        }}
                                        color='primary'
                                        sx={{color: 'primary.main'}}
                                    />
                                </Grid> : undefined}
                            </> : undefined}


                            <Grid item xs={12} sm={12}>
                                <Typography variant='h4'>תשלומים</Typography>
                            </Grid>
                            {payments.map((p, i) => (
                                <Grid item xs={12} sm={12} key={p.id}>

                                    <Typography style={{marginBottom: 9}} variant='h5'>תשלום מס' {i + 1}</Typography>
                                    <PaymentComp
                                        payment={p}
                                        onPaymentChange={onPaymentChange}
                                        onPaymentAdd={onPaymentAdd}
                                        onPaymentDelete={onPaymentDelete}
                                        addable={i === payments.length - 1}
                                        deletable={i !== 0}
                                        images={images}
                                        setImages={setImages}
                                        onCreditCharged={onCreditCharged}
                                        customerId={String(id ?? upid)}
                                        customerDetails={details}
                                    />

                                </Grid>

                            ))}
                        </Grid>
                    </Grid> : undefined}
                <Grid item xs={narrow ? 12 : 4}>
                    <Grid container>
                        <Grid item xs={12} sm={12}>
                            <DocumentPreview
                                document={
                                    {
                                        entId: customer.id,
                                        date: date ?? new Date(),
                                        entName: customer.name,
                                        type: sup ? 'sup_receipt' :'receipt',
                                        payments: (createCustomerDocument.data as PayNote)?.payments ?? payments,
                                        receiptsIds: (createCustomerDocument.data as PayNote)?.receiptsIds ?? receipts.map((d) => d.id.toString()),
                                        receiptsDisplayIds: receipts.map((d) => displayDocumentId(d)),
                                        status: createCustomerDocument.data?.status ?? 'pending_approval',
                                        id: createCustomerDocument.data?.id ?? 0,
                                        end: new Date(),
                                        color: '',
                                        textColor: '',
                                        baseColor: '',
                                        description: '',
                                        title: (createCustomerDocument.data as PayNote)?.title ?? 'קבלה חדשה',
                                        allDay: false,
                                        nkObject: (createCustomerDocument.data as PayNote)?.nkObject ?? undefined,
                                        agent: JSDB().currentAgent,
                                        name: '',
                                        issueDate: new Date(),
                                        onPaymentChange: onPaymentCHangeReal,
                                        mainValue: roundDecimals(payments.reduce((acc, p) => acc + p.amount, 0), 4),
                                        details: details,
                                        comment: comment,
                                        entName: '',
                                        recepitValue: roundDecimals(receipts.reduce((acc, p) => acc + (p.type == 'invoice' || p.type=='sup_invoice' ? p.value_left : -p.value_left), 0), 4),
                                        creditRecepitValue: creditRecepitValue,
                                        setCreditRecepitValue: setCreditRecepitValue,
                                        pool:pool
                                    }
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <Box
                                onClick={() => {
                                    if (receipts.length == 1 && receipts[0].type == 'refund') {
                                        setDisplayEmptyErr(true);
                                    }
                                }}
                                sx={{display: 'flex', flex: 1, flexDirection: 'row', justifyContent: 'space-evenly'}}>
                                <LoadingButton

                                    onClick={() =>
                                        Swal.fire({
                                            title: 'האם תרצה להנפיק קבלה?',
                                            icon: 'warning',
                                            showCancelButton: true,
                                            confirmButtonColor: theme.palette.primary.dark,
                                            cancelButtonColor: theme.palette.error.light,
                                            confirmButtonText: 'הנפק קבלה',
                                            cancelButtonText: 'בטל'
                                        }).then((result) => {
                                            if (result.isConfirmed) {
                                                issueReceipt(payments);
                                            }
                                        })
                                    }
                                    loading={createCustomerDocument.isLoading}
                                    disabled={(receipts.length == 1 && receipts[0].type == 'refund') || createCustomerDocument.isSuccess || payments.reduce((acc, p) => acc + p.amount, 0) == 0}
                                    variant='contained'
                                    type='submit'
                                    fullWidth
                                    sx={{m: 2}}
                                >
                                    הנפק
                                </LoadingButton>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Dialog fullWidth open={previewOpen.open} onBackdropClick={onPreviewClose}
                    sx={{'& .MuiDialog-paper': {p: 0}}}>
                <DocumentPreview document={previewOpen.document} icons={false} pool={pool}/>
            </Dialog>
            <Snackbar
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
                open={displayEmptyErr} autoHideDuration={3000} onClose={() => {
                setDisplayEmptyErr(false);

            }}>
                <Alert onClose={() => {
                    setDisplayEmptyErr(false);
                }} severity="error"
                       sx={{width: '100%', backgroundColor: "#ff7373", color: "black", fontWeight: "bold"}}>
                    {'לא ניתן להוציא קבלה על חשבונית זיכוי בלבד.'}
                </Alert>
            </Snackbar>
        </MainCard>
    );
};
export default memo(NewReceipt);
